import _ from 'underscore';
import state from '@bingads-webui-component-legacy/state/index';
import CampaignStateManager from '@bingads-webui-component-legacy/statemanager/campaignStateManager';
import Preferences from '@bingads-webui-component-legacy/preference/service/index';
import configComponent from '@bingads-webui-component-legacy/config/index';
import { PaginationView } from '@bingads-webui-component-legacy/github/modules/pagination-control/module/pagination-control';
import defaultTemplate from '@bingads-webui-component-legacy/bingads-grid/template/pagination.pug';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!component/bingads-grid');

const DEFAULT_PAGE_SIZES = [20, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 20;

const patchTemplate = (template, defaults) => (options) => template(_.defaults(options, defaults));

function normalizeConfig({
  el,
  pageSize,
  pageNumber,
  availablePageSizes,
  template = defaultTemplate,
  enableRandomPage,
} = {}) {
  const config = {
    el,
    pageSize,
    pageNumber,
    availablePageSizes,
    template: patchTemplate(template, {
      pageNumberLabelText: i18n.get('Grid_Page_Colon'),
    }),
    pageSizeText: i18n.get('Grid_ShowRows'),
    totalPageCountText: i18n.get('Grid_PageOf'),
    enableRandomPage,
  };

  // Normalize the pageSize and availablePageSizes
  if (!_.isFinite(pageSize)) {
    if (_.isFinite(_.first(availablePageSizes))) {
      config.pageSize = _.first(availablePageSizes);
    } else {
      config.availablePageSizes = DEFAULT_PAGE_SIZES;
      config.pageSize = DEFAULT_PAGE_SIZE;
    }
  } else if (!_.isFinite(_.first(availablePageSizes))) {
    config.availablePageSizes = [pageSize];
  }

  // Normalize the pageNumber
  if (!_.isFinite(pageNumber)) {
    config.pageNumber = 0;
  }

  config.viewModelDecorator = (viewModel) => {
    /* eslint-disable no-param-reassign */
    viewModel.onKeyPress = (data, event) => {
      const keyCode = event.which || event.keyCode;

      if (keyCode === 13) {
        event.target.blur();
        return false;
      }

      return true;
    };
    /* eslint-enable no-param-reassign */
  };

  return config;
}

/**
 * @typedef PaginationConfig
 * @type {object}
 * @property {HTMLElement|jQuery|string} el - The element of the pagination control.
 * The pagination control will be rendered in table footer when omitted.
 * @property {number} pageSize - The initial page size.
 * @property {number} pageNumber - The initial page number.
 * @property {number[]} availablePageSizes - The available page sizes user can chooser.
 * @property {function} template - The customized pagination control template.
 * @property {string} pageSizeText - The localized string before page size selector.
 * @property {string} totalPageCountText - The localized string before total page size.
 */

function updateGridViewHandler(gridView, paginationView) {
  const { pageSize, pageNumber } = paginationView;
  const skip = pageSize * pageNumber;
  const take = pageSize;

  gridView.set({
    query: _.defaults({ skip, take }, gridView.get('query')),
  }, () => {
    paginationView.trigger('change.complete:pagination');
  });
}

function updatePaginationViewHandler(gridView, paginationView) {
  const itemCount = gridView.getItemCount();

  paginationView.itemCount = itemCount;
  const { pageSize, pageNumber } = paginationView;
  const skip = pageSize * pageNumber;

  if (itemCount !== 0 && itemCount <= skip) {
    paginationView.pageNumber = 0;
  }
}

function sortChangedHandler(paginationView, gridView, changes ) {
  if (_.has(changes, 'sortableHeader')) {
    if (paginationView.pageNumber) {
      paginationView.pageNumber = 0;
    }
  }
}

function setPageNumberToState(number, name) {
  state.set('pgridPageNumber', _.defaults({
    [name]: number,
  }, state.get('pgridPageNumber') || {}));
}

export default (definePlugin) => definePlugin('pagination', [
  'config',
  'gridView',
], ({ plugins, name } = {}, gridView) => {
  const pagination = _.result(plugins, 'pagination', {});
  const paginationView = new PaginationView(normalizeConfig(pagination));

  // [todo: hezhan] remove line below, tracking by bug 820684
  paginationView.delegateEvents({'submit': () => false});
  const updatePaginationView = _.partial(updatePaginationViewHandler, gridView, paginationView);
  const updateGridView = _.partial(updateGridViewHandler, gridView, paginationView);

  updateGridView();

  setPageNumberToState(0, name);

  gridView.on('willUpdate', _.partial(sortChangedHandler, paginationView, gridView));
  gridView.on('didUpdate', updatePaginationView);
  paginationView.on('change:page-number', (pageNumber) => {
    setPageNumberToState(pageNumber, name);
    updateGridView();
  });
  paginationView.on('change:page-size', (pageSize) => {
    let data = {
      rowsPerPage: pageSize,
    };
    const usePreferencesService = _.result(configComponent.get('Permissions'), 'IsUsePreferencesServiceEnabled');

    if (usePreferencesService === true) {
      let prefix = configComponent.get('StateManagerKeyPrefix');
      let preferenceData = {
        Name: `${prefix}grid`,
        Data: data,
      };

      Preferences.setAtUserLevel(preferenceData);
    } else {
      CampaignStateManager.setItem('permanent', 'grid', data);
    }

    updateGridView();
  });

  // The plugin should render its views, user SHOULD NOT call render() again.
  paginationView.render();

  if (!pagination.el) {
    gridView.appendFootRows([{
      classes: ['noborder'],
      view: paginationView,
    }]);
  }

  return paginationView;
});
